import React, { useState, useEffect } from "react";
import { TabsContent } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, BookOpen, Building } from "lucide-react";
import { format } from "date-fns";


const UpcomingExamsTab = ({ pendingExams, selectedCompany, companies, onStartExam }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute to refresh button states
  useEffect(() => {
    // Check if any exam is starting very soon (within 2 minutes) or soon (within 10 minutes)
    const hasExamStartingVerySoon = pendingExams?.some(assignment => {
      if (!assignment.dateOfExam) return false;
      const examDate = new Date(assignment.dateOfExam);
      const now = new Date();
      const timeDiff = examDate.getTime() - now.getTime();
      return timeDiff > 0 && timeDiff <= 2 * 60 * 1000; // Within 2 minutes
    });

    const hasExamStartingSoon = pendingExams?.some(assignment => {
      if (!assignment.dateOfExam) return false;
      const examDate = new Date(assignment.dateOfExam);
      const now = new Date();
      const timeDiff = examDate.getTime() - now.getTime();
      return timeDiff > 0 && timeDiff <= 10 * 60 * 1000; // Within 10 minutes
    });

    // Use different update intervals based on proximity to exam time
    let updateInterval = 60000; // Default: 1 minute
    if (hasExamStartingVerySoon) {
      updateInterval = 1000; // 1 second for very close exams
    } else if (hasExamStartingSoon) {
      updateInterval = 30000; // 30 seconds for soon exams
    }

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, updateInterval);

    return () => clearInterval(timer);
  }, [pendingExams]);

  const selectedCompanyObj = companies?.find((c) => c.id === selectedCompany);
  const selectedCompanyName = selectedCompanyObj?.name || "Unknown Company";

  const getExamUrgency = (assignment) => {
    if (!assignment.dateOfExam) return null;
    
    const examDate = new Date(assignment.dateOfExam);
    const timeDiff = examDate.getTime() - currentTime.getTime();
    
    if (currentTime >= examDate) {
      return 'ready'; // Can start now (at or after exam time)
    } else if (timeDiff <= 10 * 60 * 1000 && timeDiff > 0) {
      return 'soon'; // Starting within 10 minutes
    }
    return null;
  };

  const canStartExam = (assignment) => {
    if (!assignment.dateOfExam) return true;

    // The stored date is in UTC, convert properly for comparison
    const examDate = new Date(assignment.dateOfExam);
    
    // Only allow starting at the exact exam time or after
    return currentTime >= examDate;
  };

  const getExamStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      ongoing: { color: "bg-blue-100 text-blue-800", text: "In Progress" },
      completed: { color: "bg-green-100 text-green-800", text: "Completed" },
      disqualified: { color: "bg-red-100 text-red-800", text: "Disqualified" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  const formatExamDateTime = (dateString, userTimezone = null) => {
    if (!dateString) return "Not scheduled";

    try {
      const examDate = new Date(dateString);

      // If we have the user's timezone from the assignment, use it
      if (userTimezone && userTimezone !== "UTC") {
        try {
          return (
            examDate.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              timeZone: userTimezone,
            }) + ` (${userTimezone})`
          );
        } catch (tzError) {
          console.error("Error formatting with timezone:", tzError);
        }
      }

      // Fallback to local time formatting
      return format(examDate, "PPP 'at' h:mm a");
    } catch (error) {
      console.error("Error formatting exam date:", error);
      return "Invalid date";
    }
  };

  const getTimeUntilExam = (dateString) => {
    if (!dateString) return null;

    try {
      const examDate = new Date(dateString);
      const diffMs = examDate.getTime() - currentTime.getTime();

      if (diffMs < 0) return "Exam available now";

      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const remainingMinutes = diffMinutes % 60;
      const remainingSeconds = diffSeconds % 60;

      // Show seconds when very close (within 2 minutes)
      if (diffMinutes < 2 && diffMinutes >= 0) {
        if (diffMinutes === 0) {
          return `Available in ${remainingSeconds}s`;
        } else {
          return `Available in ${diffMinutes}m ${remainingSeconds}s`;
        }
      } else if (diffHours > 0) {
        return `Available in ${diffHours}h ${remainingMinutes}m`;
      } else if (diffMinutes > 0) {
        return `Available in ${diffMinutes}m`;
      } else {
        return "Available now";
      }
    } catch (error) {
      console.error("Error calculating time until exam:", error);
      return "Time calculation error";
    }
  };

  if (!selectedCompany || selectedCompany === "") {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Select a Company</h3>
            <p className="text-gray-600 text-center">
              Please select a company from the dropdown to view upcoming exams.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pendingExams || pendingExams.length === 0) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
            <p className="text-gray-600 text-center">
              No upcoming exams for the selected company.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        {pendingExams.map((assignment) => {
          const canStart = canStartExam(assignment);
          const timeUntilExam = getTimeUntilExam(assignment.dateOfExam);
          const urgency = getExamUrgency(assignment);

          return (
            <Card
              key={assignment._id}
              className={`hover:shadow-md transition-shadow ${
                urgency === 'ready' 
                  ? 'border-green-500 bg-green-50' 
                  : urgency === 'soon' 
                  ? 'border-yellow-500 bg-yellow-50' 
                  : ''
              }`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {assignment.examId?.name || "Unknown Exam"}
                    </CardTitle>
                    <CardDescription>
                      {assignment.examId?.description ||
                        "No description available"}
                    </CardDescription>
                    <div className="flex items-center gap-2 mt-2">
                      <Building className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {selectedCompanyName}
                      </span>
                    </div>
                  </div>
                  {getExamStatusBadge(assignment.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                    <div className="flex flex-col">
                      <span className="text-sm">
                        {formatExamDateTime(
                          assignment.dateOfExam,
                          assignment.userTimezone
                        )}
                      </span>
                      {timeUntilExam && (
                        <span className="text-xs text-gray-500 mt-1">
                          {timeUntilExam}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="text-sm">
                      {assignment.totalTimeAllotted || 0} minutes
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Button
                    onClick={() => onStartExam(assignment._id)}
                    disabled={!canStart}
                    className={`w-full md:w-auto ${
                      !canStart 
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300" 
                        : "bg-primary hover:bg-primary/90"
                    }`}
                  >
                    {canStart ? "Start Exam" : "Not Available Yet"}
                  </Button>

                  {assignment.dateOfExam && !canStart && (
                    <span className="text-xs text-gray-500 ml-4">
                      Available at exact exam time
                    </span>
                  )}
                  
                  {assignment.dateOfExam && canStart && (
                    <span className="text-xs text-green-600 ml-4 font-medium">
                      Ready to start!
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingExamsTab;
